import axios, { AxiosPromise, ResponseType } from "axios";
import { getSecureStorage, saveSecureStorage } from "./storage";
// @ts-expect-error
import { REACT_APP_BACKEND_URL } from "@env";
import { logout } from "../redux/auth/authActions";
import store from "../redux/store";
import moment from "moment";
import { formatSaveStorage } from "./parseData";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import dayjs from "dayjs";

dayjs.extend(utc);
dayjs.extend(timezone);

type methodTypes = "post" | "get" | "put" | "patch" | "delete"

type requestType = {
  method: methodTypes;
  endpoint: string;
  data?: any;
  options?: OptionsI;
}

let locked = false;
let request: requestType;

axios.interceptors.request.use(async (config) => {
  const { refresh_expires_in } = (await getSecureStorage("user")) || {};

  if (refresh_expires_in > 0) {
    const diff = moment(refresh_expires_in).diff(moment(Date.now()), "minutes");

    if (diff <= 1 && !locked) await refreshToken();
  }

  return config;
});

axios.interceptors.response.use(
  (resp) => resp,
  async (rej) => {
    if (rej.response.status === 401) {
      if ((await refreshToken())) {
        return {
          data: Promise.resolve(Fetch(request.method, request.endpoint, request.data, request.options)),
        };
      }
    }

    return rej;
  },
);

const refreshToken = async () => {
  locked = true;
  const user = (await getSecureStorage("user")) || {};
  const { refresh_token } = user;

  return await axios
    .post(`${REACT_APP_BACKEND_URL}auth/refresh-token`, { refresh_token })
    .then(async (res: any) => {
      locked = false;
      if (String(res.status).startsWith("20")) {
        const newUser = await formatSaveStorage({ ...user, ...res.data.data });

        saveSecureStorage(newUser, "user");

        return true;
      }
      store.dispatch(logout());

      return false;
    })
    .catch((err) => {
      locked = false;
      if (String(err.response.status).startsWith("4")) {
        store.dispatch(logout());
      }

      return false;
    });
};

const errorDataParsing = (data: any) => ({
  code    : data.code,
  message :
      data.code === "TOKEN_EXPIRED" ?
        "Session expired" :
        data.message || "Something went wrong. Please contact support",
});

export interface OptionsI {
  authorization?: string;
  externalUrl?: string;
  contentType?: string;
  deleteAuth?: boolean;
  apiKey?: string;
  responseType?: ResponseType;
}

const parseTimezoneOffset = (offset: number): string => {
  const minuteSide = offset % 1 !== 0 ? Math.abs(offset % 1) * 60 : 0;
  const hourSide = offset % 1 !== 0 ? Math.floor(offset) : offset;

  return `${hourSide >= 0 ? "+" : "-"}${Math.abs(hourSide) > 9 ? Math.abs(hourSide) : `0${Math.abs(hourSide)}`}:${
    minuteSide || "00"
  }`;
};

export const Fetch = async (
  method: methodTypes,
  endpoint: string,
  data: any | null = null,
  options: OptionsI = {},
): Promise<AxiosPromise<any>> => {
  const {
    authorization,
    externalUrl,
    contentType = "application/json",
    deleteAuth = false,
    responseType = undefined,
  } = options;

  request = { method, endpoint, data, options };

  const url = externalUrl ? `${externalUrl}${endpoint}` : `${REACT_APP_BACKEND_URL}${endpoint}`;
  const { access_token, timezone: tz } = (await getSecureStorage("user")) || {};
  const offset = tz?.timezone ? dayjs().tz(tz.timezone).utcOffset() : dayjs().utcOffset();

  const Authorization = authorization || `Bearer ${access_token || null}`;
  // @ts-expect-error

  return axios({
    url,
    headers: {
      ...(offset ? { "x-timezone": parseTimezoneOffset(offset / 60) } : null),
      "content-type": contentType,
      ...(!deleteAuth && { Authorization }),
    },
    method,
    data,
    responseType,
  })
    .then((res) => {
      if (!res.data) return Promise.reject(res);

      return res.data;
    })
    .catch(async ({ response }) => {
      const data = response?.data;

      if (
        data?.code === "invalid_token"
        || data?.code === "USER_NOT_FOUND"
        || data?.code === "credentials_bad_format"
      ) {
        store.dispatch(logout());
      }
      const { code, message } = errorDataParsing(data);

      return Promise.reject({
        code,
        message,
      });
    });
};
